/** Customer-facing scope and actionable caveats, projected from the saved quote. */
import type { NormalizedBrief } from "../../../api/types.ts";

/** One compact line of what the assessor read. Chips, never token maths. */
export function chipsFor(norm: NormalizedBrief | null): string[] {
  if (!norm) return [];
  const chips: string[] = [];
  const types: Record<string, string> = { ugc: "UGC", motion: "Motion", image: "Image", mashup: "Mashup" };
  if (norm.cardType) chips.push(types[norm.cardType] ?? norm.cardType);
  const hasScript = !!norm.spokenScript?.trim() && !norm.flags?.includes("no_script");
  if (norm.cardType !== "image" && (norm.finishedSeconds ?? 0) > 0) {
    chips.push(`${hasScript ? "Est. voice-over" : "Est. duration"} · ${norm.finishedSeconds}s`);
  }
  if (norm.deliverables) {
    const d = norm.deliverables;
    const unit = hasScript ? "script" : "creative";
    if (d.distinct > 0) chips.push(`${d.distinct} ${unit}${d.distinct === 1 ? "" : "s"}`);
    if (d.variants > 0) chips.push(`Aditor Hooks · ${d.variants}`);
  }
  if (norm.decisions?.sopCoverage === "standard" && !norm.flags?.includes("sop_unknown") &&
      !norm.decisions.guessedFactors?.includes("sopCoverage")) {
    chips.push("✓ Covered by our SOPs");
  }
  if (norm.source === "swipe") chips.push("From a swipe");
  return chips;
}

/** Actionable scope caveats only. Internal diagnostics belong in the admin. */
export function readingLine(norms: (NormalizedBrief | null)[]): string | null {
  const words: Record<string, string> = {
    low_confidence: "Please check that the scope above matches your brief.",
    no_script: "No spoken script found: the duration is an estimate. Add the script for a more accurate quote.",
    split_fallback: "We read this document as one creative. If it contains separate ads, edit the brief before approving.",
  };
  const flags = [...new Set(norms.flatMap(n => n?.flags ?? []))];
  const messages = flags.map(f => words[f]).filter(Boolean);
  return messages.length ? messages.join(" ") : null;
}
